import { defineMessages } from 'react-intl';
import { push as updatePath } from 'react-router-redux';
import { SubmissionError, change, initialize, startSubmit, stopSubmit } from 'redux-form';
import { all, call, put, select, spawn, takeEvery } from 'redux-saga/effects';
import { loadAccountCreationConsent } from 'components/Consent/Consent.saga';
import { trackGoogleTagManagerEvent } from 'components/GoogleTagManager/GoogleTagManager';
import { selectIsPaymentMethodSepaCompatible } from 'components/PaymentMethodSelector/PaymentMethodSelector.reducer';
import {
  ERROR_INVALID_REQUEST,
  ERROR_SIGNUP_EXISTING_EMAIL,
  ERROR_SIGNUP_FB_EXISTING_ACCOUNT,
  ERROR_SIGNUP_FB_MISSING_EMAIL,
} from 'constants/api-error-codes';
import { authorizeFacebook, doLoginWithFacebook, getFacebookProfile } from 'features/Login/Authentication.saga';
import { userNeedsPhoneVerification } from 'features/PhoneVerification/PhoneVerification.saga';
import { getActiveProduct } from 'features/Products/Products.helpers';
import { deepExtract } from 'helpers/deep';
import { augmentSubmitActionWithAdditionalData, getFormFieldInitialValue, getFormValues } from 'helpers/forms';
import { getSelectorHtml } from 'helpers/getSelectorHtml';
import { ga } from 'helpers/multitrack';
import { trackOptimizelyEvent } from 'helpers/optimizely';
import { segment } from 'helpers/segment';
import { getForApi as getUtmValues } from 'helpers/utmValues';
import { getUserLocation, gtmSafeRedirect, scrollToTop } from 'redux/saga/helpers';
import { friendbuyCode } from 'redux/saga/selectors/friendbuy';
import { selectUserLocale } from 'redux/modules/locale';
import { pathSelector } from 'redux/saga/selectors/request';
import { signup } from './FormAction';

export const messages = defineMessages({
  errorCreatingAccount: {
    id: 'signup.error.unableToCreateAccount',
    defaultMessage:
      'Sorry, we’re unable to create your account at this time. Please, <a href="{contact}">reach out to us</a> for account help.',
    values: {
      contact: '/contact',
    },
  },
  existingAccount: {
    id: 'signup.error.existingAccount',
    defaultMessage:
      'Looks like you already started creating a ClassPass account with this email address in the past. Please <a href="{loginUrl}">log in</a> to complete your purchase.',
  },
  noFacebookEmail: {
    id: 'signup.error.noFacebookEmail',
    defaultMessage: 'Your Facebook account must have an email to sign up. Please add one or sign up via email.',
  },
  typoInEmail: {
    id: 'signup.error.typoInEmail',
    defaultMessage: 'Please check your email address for misspellings.',
  },
});

export const FORM_NAME = 'signup';

// Action names
const SIGNUP_WITH_FACEBOOK = 'SIGNUP.SIGNUP_WITH_FACEBOOK';

// Actions
export const signupWithEmail = augmentSubmitActionWithAdditionalData(signup, { type: 'email' });
export const signupWithFacebook = () => ({ type: SIGNUP_WITH_FACEBOOK });

// State selectors
export const getInitialValueMarketingOptOut = getFormFieldInitialValue(FORM_NAME, 'marketingOptOut');
export const getInitialValueMarketing = getFormFieldInitialValue(FORM_NAME, 'marketingOptIn');
export const getInitialValueSearchable = getFormFieldInitialValue(FORM_NAME, 'searchable');

export function* loadSignupData(client) {
  const consent = yield call(loadAccountCreationConsent, client);
  const ActiveProduct = yield select(getActiveProduct);

  yield all([
    put(initialize(FORM_NAME, consent)),
    put(change(FORM_NAME, 'product', ActiveProduct.key)),
    call(ActiveProduct.loadSignupData, client),
    spawn(ActiveProduct.trackSignupLoad),
  ]);
}

export function* postSignupTracking(signupType, profile, userId) {
  const gaEventName = signupType === 'facebook' ? 'Sign Up by Facebook' : 'Sign Up by Email';
  yield all([
    call(trackOptimizelyEvent, 'create_account'),
    call(segment.track, 'account_created', {
      email_address: profile.email,
      userId,
    }),
    call(trackGoogleTagManagerEvent, {
      event: 'account_created',
      email_address: profile.email,
      userId,
    }),
    call(segment.track, gaEventName, {
      category: 'Membership Purchase',
      label: userId,
    }),
    call(ga, 'Membership Purchase', gaEventName, userId),
  ]);
}

export function* loadSepaConsent() {
  const activePaymentMethodIsSepaCompatible = yield select(selectIsPaymentMethodSepaCompatible);

  if (!activePaymentMethodIsSepaCompatible) {
    return;
  }
  const sepaLegalCopy = yield call(getSelectorHtml, '.sepa-terms');

  return {
    legal_copy: sepaLegalCopy,
    action: { form_type: 'submit' },
    consent_type: 'sepa_direct_debit_mandate',
    is_granted: true,
  };
}

export function* doSignupSubmission(client, { payload: formPayload }) {
  const ActiveProduct = yield select(getActiveProduct);
  const activeLocation = yield call(getUserLocation, client);
  const sepaConsent = yield call(loadSepaConsent);
  const terms = yield call(getSelectorHtml, '.SignupForm__terms__text span');
  const signupPayload = {
    conversion: yield call(getUtmValues),
    consents: [
      {
        legal_copy: terms, // Terms is a required field in CMS so will always be defined
        action: { form_type: 'submit' },
        consent_type: ['terms_of_use', 'privacy_policy'],
        is_granted: true,
      },
      ...(sepaConsent ? [sepaConsent] : []),
    ],
    ...(activeLocation && { gls_location_id: deepExtract('location.id', activeLocation) }),
  };

  // TO PREVENT CART ABANDONERS FROM SIGNING UP FOR RAF PLANS WITHOUT CORRECT FRIENDBUY CODE:
  signupPayload.conversion.utm = {
    ...(signupPayload.conversion ? signupPayload.conversion.utm : {}),
    utm_term: yield select(friendbuyCode),
  };

  try {
    const isEmailSignup = formPayload.type === 'email';
    const locale = yield select(selectUserLocale);

    if (isEmailSignup) {
      Object.assign(signupPayload, {
        user: {
          first_name: formPayload.firstName,
          last_name: formPayload.lastName,
          email: formPayload.email,
          password: formPayload.password,
          locale,
        },
        newsletter_subscribed: !!formPayload.marketingOptIn,
      });

      // marketingOptOutDisclaimer or marketingDisclaimer is only shown to members in some countries
      if (formPayload.marketingOptOutDisclaimer) {
        const initialValueMarketingOptOut = yield select(getInitialValueMarketingOptOut);
        const marketingOptOutDisclaimer = yield call(getSelectorHtml, '.marketing-disclaimer-toggle > .media__body');

        signupPayload.consents.push({
          legal_copy: marketingOptOutDisclaimer,
          action: {
            form_type: 'checkbox',
            form_default: initialValueMarketingOptOut ? 'checked' : 'unchecked',
          },
          consent_type: ['marketing_emails'],
          is_granted: !formPayload.marketingOptOut, // flip the logic from opt out to opt in
        });
        signupPayload.newsletter_subscribed = !formPayload.marketingOptOut; // flip the logic from opt out to opt in
      } else if (formPayload.marketingOptInDisclaimer) {
        const initialValueMarketing = yield select(getInitialValueMarketing);
        const marketingOptInDisclaimer = yield call(getSelectorHtml, '.marketing-disclaimer-toggle > .media__body');
        signupPayload.consents.push({
          legal_copy: marketingOptInDisclaimer,
          action: {
            form_type: 'checkbox',
            form_default: initialValueMarketing ? 'checked' : 'unchecked',
          },
          consent_type: ['marketing_emails'],
          is_granted: signupPayload.newsletter_subscribed,
        });
      }
    } else {
      try {
        Object.assign(signupPayload, {
          facebook: {
            token: yield call(authorizeFacebook),
          },
        });
        // facebookDisclaimer is only shown to members in some countries
        const facebookDisclaimer = yield call(getSelectorHtml, '.facebook-disclaimer > span');

        if (formPayload.facebookDisclaimer) {
          signupPayload.consents.push({
            legal_copy: facebookDisclaimer,
            action: { form_type: 'submit' },
            consent_type: ['marketing_emails'],
            is_granted: true,
          });
        }
      } catch (err) {
        const error = new SubmissionError({ _error: err.message });
        yield all([
          spawn(ActiveProduct.trackSignupFailure),
          put(stopSubmit(FORM_NAME, error)),
          put(signup.failure(error)),
        ]);
        return;
      }
    }

    const initialValueSearchable = yield select(getInitialValueSearchable);
    const searchableDisclaimer = yield call(getSelectorHtml, '.searchable-disclaimer-toggle > .media__body');
    signupPayload.consents.push({
      legal_copy: searchableDisclaimer,
      action: {
        form_type: 'checkbox',
        form_default: initialValueSearchable ? 'checked' : 'unchecked',
      },
      consent_type: ['social_search'],
      is_granted: !!formPayload.searchable,
    });

    const userId = yield call(ActiveProduct.submitSignupData, client, signupPayload);
    const profile = isEmailSignup
      ? signupPayload.user
      : yield call(getFacebookProfile, (client && client.FB) || window.FB);

    yield spawn(ActiveProduct.trackSignupSuccess);
    yield call(postSignupTracking, formPayload.type, profile, userId);

    const phoneVerificationEnabled = yield call(userNeedsPhoneVerification, client);
    const nextStep = phoneVerificationEnabled ? 'verify-phone' : 'purchase';

    if (phoneVerificationEnabled) {
      yield put(change('verify-phone-number', 'isNewUser', true));
    }

    yield put(signup.success());
    const path = yield select(pathSelector);
    yield put(updatePath(path.replace(/^\/signup/, `/${nextStep}`)));
  } catch (err) {
    let message = { _error: messages.errorCreatingAccount };

    if (formPayload.type === 'facebook' && err.code === ERROR_SIGNUP_FB_EXISTING_ACCOUNT) {
      yield call(doLoginWithFacebook, client, null, signupPayload.facebook.token);
      yield call(gtmSafeRedirect, '/');
      return;
    }
    if (err.code === ERROR_SIGNUP_FB_MISSING_EMAIL) {
      message = { _error: messages.noFacebookEmail };
    } else if (err.code === ERROR_SIGNUP_EXISTING_EMAIL) {
      const path = yield select(pathSelector);
      const loginUrl = `/login?returnTo=${encodeURIComponent(path)}`;
      message = {
        _error: {
          ...messages.existingAccount,
          values: {
            loginUrl,
          },
        },
      };
    } else if (err.code === ERROR_INVALID_REQUEST) {
      message = { email: messages.typoInEmail };
    } else if (window.Rollbar) {
      window.Rollbar.error(err);
    }

    const errorObj = typeof message === 'string' ? { _error: message } : message;
    const error = new SubmissionError(errorObj);
    yield all([
      spawn(ActiveProduct.trackSignupFailure),
      call(scrollToTop),
      put(signup.failure(error)),
      put(stopSubmit(FORM_NAME, errorObj)),
    ]);
  }
}

export function* doFacebookSignup(client) {
  yield put(startSubmit(FORM_NAME));
  yield call(doSignupSubmission, client, {
    payload: {
      type: 'facebook',
      ...(yield select(getFormValues(FORM_NAME))),
    },
  });
}

export function* watchSignupSagas(client) {
  yield takeEvery(signup.REQUEST, doSignupSubmission, client);
  yield takeEvery(SIGNUP_WITH_FACEBOOK, doFacebookSignup, client);
}
